import React, { useLayoutEffect, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { ACTIVE_JOB_STATUSES, StatusValue } from 'api/jobApi';
import { getStatusColor } from 'util/statusColors';
import { chartTokens, pillSelectSx } from './dashboardChartTokens';

/** Rows of filter chips shown before the rest collapse into a "+n" badge. */
const MAX_CHIP_ROWS = 2;

export const allStatus = [
  {
    label: StatusValue.NOT_STARTED,
    color: getStatusColor(StatusValue.NOT_STARTED, 200),
  },
  {
    label: StatusValue.IN_PROGRESS,
    color: getStatusColor(StatusValue.IN_PROGRESS, 700),
  },
  {
    label: StatusValue.ON_HOLD,
    color: getStatusColor(StatusValue.ON_HOLD, 300),
  },
  {
    label: StatusValue.APPROVAL_PENDING,
    color: getStatusColor(StatusValue.APPROVAL_PENDING, 700),
  },
  {
    label: StatusValue.TO_BE_INVOICED,
    color: getStatusColor(StatusValue.TO_BE_INVOICED, 600),
  },
  {
    label: StatusValue.COMPLETED,
    color: getStatusColor(StatusValue.COMPLETED, 800),
  },
  {
    label: StatusValue.CANCELLED,
    color: getStatusColor(StatusValue.CANCELLED, 700),
  },
  {
    label: StatusValue.CUSTOMER_CONTACTED,
    color: getStatusColor(StatusValue.CUSTOMER_CONTACTED, 500),
  },
  {
    label: StatusValue.INITIAL_REPORT_SUBMITTED,
    color: getStatusColor(StatusValue.INITIAL_REPORT_SUBMITTED, 700),
  },
  {
    label: StatusValue.PROGRESS_REPORT_SUBMITTED,
    color: getStatusColor(StatusValue.PROGRESS_REPORT_SUBMITTED, 700),
  },
  {
    label: StatusValue.INVOICED,
    color: getStatusColor(StatusValue.INVOICED, 800),
  },
];

export const statusColorOf = (status: StatusValue) =>
  allStatus.find((ob) => ob.label === status)?.color ?? chartTokens.accent;

/** True when `selected` is exactly the active set, in any order. */
export const isActiveSelection = (selected: StatusValue[]) =>
  selected.length === ACTIVE_JOB_STATUSES.length &&
  ACTIVE_JOB_STATUSES.every((status) => selected.includes(status));

export const StatusFilterChip = ({
  status,
  onDelete,
}: {
  status: StatusValue;
  onDelete: () => void;
}) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      height: 36,
      paddingLeft: '12px',
      paddingRight: '8px',
      borderRadius: '999px',
      backgroundColor: chartTokens.accentMuted,
      color: chartTokens.accent,
      fontSize: 14,
      fontWeight: 500,
    }}>
    <FiberManualRecordIcon sx={{ fontSize: 10, color: statusColorOf(status) }} />
    {status}
    <CloseIcon
      role="button"
      aria-label={`Remove ${status} filter`}
      onMouseDown={(event) => {
        // Stop the parent Select from opening when the chip is dismissed.
        event.preventDefault();
        event.stopPropagation();
        onDelete();
      }}
      sx={{
        fontSize: 16,
        cursor: 'pointer',
        opacity: 0.7,
        '&:hover': { opacity: 1 },
      }}
    />
  </Box>
);

const OverflowChip = ({ count }: { count: number }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      height: 36,
      paddingX: '12px',
      borderRadius: '999px',
      border: `1px dashed ${chartTokens.accent}`,
      color: chartTokens.accent,
      fontSize: 14,
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
    {`+${count} more`}
  </Box>
);

/**
 * Lays the selected statuses out as chips, keeping them to `MAX_CHIP_ROWS`
 * rows. Whatever does not fit collapses into a "+n more" chip. The chips are
 * measured off-screen at the real container width so the cut-off follows the
 * available space rather than a fixed chip count.
 */
const StatusChipRow = ({
  statuses,
  onRemove,
}: {
  statuses: StatusValue[];
  onRemove: (status: StatusValue) => void;
}) => {
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(statuses.length);

  useLayoutEffect(() => {
    const container = measureRef.current;
    if (!container) return undefined;

    const measure = () => {
      const chips = Array.from(container.children) as HTMLElement[];
      if (!chips.length) return;

      const rowTops: number[] = [];
      const rowOf = (chip: HTMLElement) => {
        const top = chip.offsetTop;
        const existing = rowTops.findIndex((ob) => Math.abs(ob - top) < 4);
        if (existing >= 0) return existing;
        rowTops.push(top);
        return rowTops.length - 1;
      };

      const fits = chips.filter((chip) => rowOf(chip) < MAX_CHIP_ROWS).length;

      // One chip makes way for the "+n more" badge when anything is dropped.
      setVisibleCount(
        fits >= chips.length ? chips.length : Math.max(fits - 1, 1)
      );
    };

    measure();

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [statuses]);

  const hidden = Math.max(statuses.length - visibleCount, 0);

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {/* Off-screen copy used only to work out how many chips fit. */}
      <Box
        ref={measureRef}
        aria-hidden
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          visibility: 'hidden',
          pointerEvents: 'none',
        }}>
        {statuses.map((status) => (
          <StatusFilterChip
            key={status}
            status={status}
            onDelete={() => undefined}
          />
        ))}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {statuses.slice(0, visibleCount).map((status) => (
          <StatusFilterChip
            key={status}
            status={status}
            onDelete={() => onRemove(status)}
          />
        ))}
        {hidden > 0 && <OverflowChip count={hidden} />}
      </Box>
    </Box>
  );
};

const PlaceholderPill = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      height: 36,
      paddingX: '12px',
      borderRadius: '999px',
      backgroundColor: chartTokens.accentMuted,
      color: chartTokens.accent,
      fontSize: 14,
      fontWeight: 500,
      whiteSpace: 'nowrap',
    }}>
    {children}
  </Box>
);

interface StatusSelectProps {
  value: StatusValue[];
  onChange: (statuses: StatusValue[]) => void;
  /**
   * `chips` lists each status as a removable chip — good for wide cards.
   * `summary` collapses the selection into a single pill for narrow ones.
   */
  variant?: 'chips' | 'summary';
  width?: number | string;
}

/**
 * Multi-select over job statuses, shared by the dashboard cards so they
 * filter and read the same way.
 */
const StatusFilterSelect = ({
  value,
  onChange,
  variant = 'chips',
  width,
}: StatusSelectProps) => {
  const handleChange = (event: SelectChangeEvent<StatusValue[]>) => {
    const selected = event.target.value;
    onChange(
      typeof selected === 'string'
        ? (selected.split(',') as StatusValue[])
        : selected
    );
  };

  const summaryLabel = !value.length
    ? 'All statuses'
    : isActiveSelection(value)
    ? 'Ongoing statuses'
    : value.length === 1
    ? value[0]
    : `${value.length} statuses`;

  return (
    <Select
      multiple
      displayEmpty
      value={value}
      onChange={handleChange}
      input={<OutlinedInput />}
      sx={{
        ...pillSelectSx,
        height: 'auto',
        minHeight: 36,
        width: width ?? 'auto',
        maxWidth: '100%',
        '& .MuiSelect-select': {
          display: 'block',
          padding: '0 32px 0 0',
        },
        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
      }}
      renderValue={(selected) => {
        if (variant === 'summary') {
          return <PlaceholderPill>{summaryLabel}</PlaceholderPill>;
        }
        return selected.length ? (
          <StatusChipRow
            statuses={selected as StatusValue[]}
            onRemove={(status) =>
              onChange(value.filter((ob) => ob !== status))
            }
          />
        ) : (
          <PlaceholderPill>All statuses</PlaceholderPill>
        );
      }}>
      {allStatus.map((status) => (
        <MenuItem key={status.label} value={status.label} sx={{ paddingY: 0 }}>
          <Checkbox
            size="small"
            checked={value.includes(status.label)}
            sx={{ color: chartTokens.muted }}
          />
          <FiberManualRecordIcon
            sx={{ fontSize: 12, color: status.color, marginRight: 1 }}
          />
          {status.label}
        </MenuItem>
      ))}
    </Select>
  );
};

export default StatusFilterSelect;
